import {
  Users,
  Activity,
  Target,
  TrendingUp,
  Clock,
  UserCheck,
  Award,
  Star
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts';

interface AcademyOverviewProps {
  stats: any;
}

export function AcademyOverview({ stats }: AcademyOverviewProps) {
  // Mock trend data for charts
  const performanceTrend = [
    { month: 'Jan', improvement: 18.2, sessions: 220, revenue: 38000 },
    { month: 'Feb', improvement: 19.8, sessions: 245, revenue: 41000 },
    { month: 'Mar', improvement: 21.1, sessions: 268, revenue: 43500 },
    { month: 'Apr', improvement: 22.4, sessions: 289, revenue: 44800 },
    { month: 'May', improvement: 23.1, sessions: 295, revenue: 45200 },
    { month: 'Jun', improvement: 23.5, sessions: 308, revenue: 47100 }
  ];

  const utilizationData = [
    { name: 'Coaches', value: stats?.utilization?.coaches || 0, color: '#3B82F6' },
    { name: 'Facilities', value: stats?.utilization?.facilities || 0, color: '#10B981' },
    { name: 'Equipment', value: stats?.utilization?.equipment || 0, color: '#F59E0B' }
  ];

  const getStatusColor = (value: number) => {
    if (value >= 80) return 'text-green-600';
    if (value >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Key Performance Indicators */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Players</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalPlayers || 0}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+12%</span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Coaches</CardTitle>
            <UserCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeCoaches || 0}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-blue-600">Avg 4.7★</span> rating
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Sessions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalSessions || 0}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+8%</span> from last month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Improvement</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.performance?.averageImprovement || 0}%</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">+2.3%</span> this quarter
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Trends */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Performance Trends</CardTitle>
            <CardDescription>
              Monthly improvement rates and session counts
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis yAxisId="improvement" orientation="left" />
                <YAxis yAxisId="sessions" orientation="right" />
                <Tooltip 
                  formatter={(value, name) => [
                    `${value}${name === 'improvement' ? '%' : ''}`, 
                    name === 'improvement' ? 'Improvement' : 'Sessions'
                  ]}
                />
                <Line 
                  yAxisId="improvement"
                  type="monotone" 
                  dataKey="improvement" 
                  stroke="#3B82F6" 
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6' }}
                />
                <Line 
                  yAxisId="sessions"
                  type="monotone" 
                  dataKey="sessions" 
                  stroke="#10B981" 
                  strokeWidth={2}
                  dot={{ fill: '#10B981' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resource Utilization</CardTitle>
            <CardDescription>
              Current utilization across academy resources
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {utilizationData.map((item) => (
                <div key={item.name} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">{item.name}</span>
                    <span className={`text-sm font-semibold ${getStatusColor(item.value)}`}>
                      {item.value}%
                    </span>
                  </div>
                  <Progress 
                    value={item.value} 
                    className="h-2"
                    style={{
                      '--progress-background': item.color
                    } as React.CSSProperties}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Performers & Recent Activity */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Award className="mr-2 h-5 w-5" />
              Top Performers
            </CardTitle>
            <CardDescription>
              Players with highest improvement this month
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.topPerformers?.slice(0, 5).map((performer: any, index: number) => (
                <div key={performer.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold text-sm">
                      #{index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{performer.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Coach: {performer.coachName}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center">
                      <Star className="h-3 w-3 text-yellow-500 mr-1" />
                      <span className="text-sm font-medium">{performer.score}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      +{performer.improvement}%
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="mr-2 h-5 w-5" />
              Recent Activity
            </CardTitle>
            <CardDescription>
              Latest achievements and milestones
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recentActivity?.slice(0, 5).map((activity: any) => (
                <div key={activity.id} className="flex items-start space-x-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-xs">
                    {activity.type === 'achievement' && <Award className="h-3 w-3" />}
                    {activity.type === 'session' && <Activity className="h-3 w-3" />}
                    {activity.type === 'milestone' && <Target className="h-3 w-3" />}
                  </div>
                  <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium">{activity.description}</p>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      {activity.playerName && (
                        <span>Player: {activity.playerName}</span>
                      )}
                      {activity.coachName && (
                        <span>Coach: {activity.coachName}</span>
                      )}
                      <span>•</span>
                      <span>{new Date(activity.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Academy Performance Metrics */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Retention Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {stats?.performance?.retention || 0}%
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Player retention over 12 months
            </p>
            <Progress value={stats?.performance?.retention || 0} className="mt-3" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Satisfaction Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600 flex items-center">
              {stats?.performance?.satisfaction || 0}
              <Star className="h-6 w-6 text-yellow-500 ml-2" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Average player & parent rating
            </p>
            <div className="flex space-x-1 mt-3">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  className={`h-4 w-4 ${
                    star <= (stats?.performance?.satisfaction || 0) 
                      ? 'text-yellow-500 fill-current' 
                      : 'text-gray-300'
                  }`} 
                />
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Facilities</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {stats?.facilitiesCount || 0}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Active training facilities
            </p>
            <div className="mt-3">
              <div className="flex justify-between text-sm">
                <span>Utilization</span>
                <span className={getStatusColor(stats?.utilization?.facilities || 0)}>
                  {stats?.utilization?.facilities || 0}%
                </span>
              </div>
              <Progress value={stats?.utilization?.facilities || 0} className="mt-1" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}